<!-- SwipFx - Enable Two-Factor Authentication (TOTP) -->
<div class="min-vh-100 bg-light">
  <app-header [page]="'Accounts'"></app-header>

  <main class="container py-4">

    <!-- Header -->
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <div>
        <h4 class="mb-0 fw-bold">Two-factor authentication</h4>
        <div class="text-muted small">Enable an authenticator app (TOTP) for extra security</div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary btn-sm" (click)="back()">Back</button>
      </div>
    </div>

    <!-- ERROR -->
    <div *ngIf="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- SUCCESS -->
    <div *ngIf="success" class="alert alert-success">
      <div class="fw-semibold">2FA enabled</div>
      <div class="small">Your authenticator app is now linked to your SwipFx account.</div>
    </div>

    <!-- LOADING SKELETON -->
    <div *ngIf="loadingSetup && !setupData" class="card shadow-sm border-0">
      <div class="card-body p-4">
        <div class="placeholder-glow">
          <div class="placeholder col-4 mb-2"></div>
          <div class="placeholder col-7 mb-4"></div>
          <div class="placeholder col-12 mb-2"></div>
          <div class="placeholder col-10 mb-2"></div>
          <div class="placeholder col-8"></div>
        </div>
      </div>
    </div>

    <div *ngIf="setupData && !success" class="row g-3">

      <!-- Left: Summary -->
      <div class="col-12 col-lg-4">
        <div class="card shadow-sm border-0">
          <div class="card-body p-4">

            <div class="d-flex align-items-center gap-3">
              <div class="rounded-circle d-flex align-items-center justify-content-center"
                   style="width:56px; height:56px; background:#0b1f3a; color:#fff; font-weight:700;">
                2FA
              </div>
              <div>
                <div class="fw-bold">Authenticator App</div>
                <div class="text-muted small">Google / Microsoft Authenticator • Authy</div>
                <div class="small mt-1">
                  <span class="badge text-bg-warning">Setup</span>
                  <span class="badge text-bg-secondary ms-1">TOTP</span>
                </div>
              </div>
            </div>

            <hr class="my-4" />

            <div class="small text-muted mb-2">Requirements</div>
            <ul class="small text-muted ps-3 mb-0">
              <li>Install an authenticator app</li>
              <li>Scan the QR code (or enter the key)</li>
              <li>Confirm using a 6-digit code</li>
            </ul>

            <hr class="my-4" />

            <div class="p-3 border rounded bg-body">
              <div class="fw-semibold mb-1">Troubleshooting</div>
              <div class="small text-muted">
                If codes don’t work, check that your phone time is set to <span class="fw-semibold">automatic</span>.
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Right: Tabs -->
      <div class="col-12 col-lg-8">
        <div class="card shadow-sm border-0">
          <div class="card-body p-0">

            <ul class="nav nav-tabs px-3 pt-3" role="tablist">
              <li class="nav-item" role="presentation">
                <button class="nav-link active" data-bs-toggle="tab" data-bs-target="#tabScan"
                        type="button" role="tab">
                  Scan QR
                </button>
              </li>

              <li class="nav-item" role="presentation">
                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#tabManual"
                        type="button" role="tab">
                  Manual key
                </button>
              </li>

              <li class="nav-item" role="presentation">
                <button class="nav-link" data-bs-toggle="tab" data-bs-target="#tabConfirm"
                        type="button" role="tab">
                  Confirm
                </button>
              </li>
            </ul>

            <div class="tab-content p-4">

              <!-- Scan QR -->
              <div class="tab-pane fade show active" id="tabScan" role="tabpanel">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <div class="fw-bold">Scan the QR code</div>
                    <div class="text-muted small">Open your authenticator app and scan the code below.</div>
                  </div>
                </div>

                <div class="row g-3">
                  <div class="col-12 col-md-6">
                    <div class="p-3 border rounded bg-body text-center">
                      <img *ngIf="qrDataUrl"
                           [src]="qrDataUrl"
                           alt="TOTP QR code"
                           class="img-fluid border rounded p-2 bg-white"
                           style="max-width: 280px;" />
                      <div *ngIf="!qrDataUrl" class="text-muted small">Generating QR…</div>
                    </div>
                    <div class="small text-muted mt-2">
                      Can’t scan? Use the <span class="fw-semibold">Manual key</span> tab.
                    </div>
                  </div>

                  <div class="col-12 col-md-6">
                    <div class="p-3 border rounded bg-body h-100">
                      <div class="fw-semibold mb-2">Steps</div>
                      <ol class="small text-muted ps-3 mb-0">
                        <li>Open your authenticator app</li>
                        <li>Tap <span class="fw-semibold">Add</span></li>
                        <li>Select <span class="fw-semibold">Scan a QR code</span></li>
                        <li>Scan the QR on the left</li>
                        <li>Go to <span class="fw-semibold">Confirm</span> and enter the code</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Manual key -->
              <div class="tab-pane fade" id="tabManual" role="tabpanel">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <div class="fw-bold">Manual entry key</div>
                    <div class="text-muted small">Enter this key in your authenticator app.</div>
                  </div>
                </div>

                <div class="p-3 border rounded bg-body">
                  <label class="form-label small text-muted">Key</label>
                  <div class="input-group">
                    <input class="form-control font-monospace"
                           [value]="setupData.manualEntryKey"
                           readonly />
                    <button class="btn btn-outline-secondary"
                            type="button"
                            (click)="copyKey(setupData.manualEntryKey)">
                      Copy
                    </button>
                  </div>

                  <div class="form-text">
                    In your app choose <span class="fw-semibold">Enter a setup key</span>,
                    type <span class="fw-semibold">Time-based</span>, digits <span class="fw-semibold">6</span>.
                  </div>

                  <hr class="my-3" />

                  <div class="small text-muted">
                    Keep this key private. Anyone with it can generate your 2FA codes.
                  </div>
                </div>
              </div>

              <!-- Confirm -->
              <div class="tab-pane fade" id="tabConfirm" role="tabpanel">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <div class="fw-bold">Confirm setup</div>
                    <div class="text-muted small">Enter the 6-digit code from your authenticator app.</div>
                  </div>
                </div>

                <form [formGroup]="confirmForm" (ngSubmit)="confirmTotp()">
                  <div class="row g-3">
                    <div class="col-12 col-md-6">
                      <label class="form-label small text-muted">6-digit code</label>
                      <input type="text"
                             inputmode="numeric"
                             autocomplete="one-time-code"
                             maxlength="6"
                             class="form-control form-control-lg text-center fw-bold"
                             placeholder="123456"
                             formControlName="code"
                             [ngClass]="{
                               'is-invalid': submitted && confirmForm.get('code')?.invalid
                             }" />

                      <div *ngIf="submitted && confirmForm.get('code')?.invalid"
                           class="invalid-feedback">
                        Please enter a valid 6-digit code
                      </div>

                      <div class="form-text">
                        Codes refresh every 30 seconds. If it fails, try the next code.
                      </div>
                    </div>

                    <div class="col-12 col-md-6">
                      <div class="p-3 border rounded bg-body h-100">
                        <div class="fw-semibold mb-1">What happens next?</div>
                        <div class="small text-muted">
                          2FA will be required on login and other high-risk actions.
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr class="my-4" />

                  <div class="d-flex flex-wrap gap-2 justify-content-end">
                    <button type="button"
                            class="btn btn-outline-secondary"
                            (click)="back()"
                            [disabled]="loadingConfirm">
                      Cancel
                    </button>

                    <button type="submit"
                            class="btn btn-primary"
                            [disabled]="loadingConfirm || confirmForm.invalid">
                      <span *ngIf="loadingConfirm" class="spinner-border spinner-border-sm me-2"></span>
                      Enable 2FA
                    </button>
                  </div>
                </form>

              </div>

            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </main>
</div>